import { z } from 'zod';
import { businessShapeSchema } from './brand-brain.js';
// Channel-intake schemas — the chat-based onboarding mag runs when a
// founder hands over an organic channel (instagram today, more later).
// Models a multi-turn conversation where mag asks one question per
// turn until she has enough signal to commit a config + draft the
// first-week post arc.
//
// Pattern mirrors campaign intake (intakeMessageSchema +
// intakeAgentResultSchema) so callers that already speak that shape
// can reuse their UI: same chat bubble layout, same submit semantics.
/** One turn in the channel intake conversation. Same shape as
 *  IntakeMessage so MagChatDrawer can render both without forking. */
export const channelIntakeMessageSchema = z.object({
    role: z.enum(['mag', 'founder']),
    body: z.string().min(1).max(4000),
    createdAt: z.string().datetime(),
});
/** Three-tier authority the founder picks at the end of intake.
 *  Replaces the existing operatedByMag bool — gives the founder a
 *  middle option ("draft + schedule but ping me to publish") instead
 *  of binary on/off. */
export const channelAuthorityTierSchema = z.enum([
    /** Mag drafts every post; founder approves each one in the queue
     *  before it ships. No scheduling done either — founder picks the
     *  publish time. The tightest leash. */
    'draft_only',
    /** Mag drafts + schedules; founder gets a "ready, posts at <time>"
     *  ping with a window to veto. Default if the founder doesn't
     *  explicitly pick — matches the channel control panel's "every
     *  post needs your call" toggle. */
    'draft_and_schedule',
    /** Mag drafts + schedules + auto-publishes. No per-post approval;
     *  founder reviews retroactively via the receipts panel. The
     *  ceiling of trust for organic channels. */
    'full_autopilot',
]);
/** ISO weekday keys (brand-tz calendar days) a founder can name as
 *  their posting cadence. Lowercase short forms so the agent emits
 *  them naturally and the UI can render them verbatim. */
export const weekdayKeySchema = z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);
/** Mag's recommended cadence — captured from the intake answer in
 *  case the founder said "2 a week" instead of giving a precise
 *  number. Bounded so a typo doesn't propose 200 posts a week. */
export const channelIntakeCadenceSchema = z.object({
    posts_per_week: z.number().int().min(1).max(14),
    /** Brand timezone the cadence is expressed in. Stamped at intake
     *  time so a later TZ change on the brand doesn't shift the
     *  schedule silently. */
    tz: z.string().min(1).max(64),
    /** Specific weekdays the founder named ("tue/thu/sat"). When set,
     *  the content-plan scheduler places slots ONLY on these days
     *  (deterministically — see channel-content-plan.ts). Empty = no
     *  day preference, mag spaces the cadence herself. Defaulted so
     *  proposals authored before this field existed still parse. */
    posting_days: z.array(weekdayKeySchema).max(7).default([]),
});
/** One audience-aligned posting window — a single hour-of-day in
 *  brand tz when this audience is most likely to engage. s216-54h.69:
 *  the content-plan agent currently picks hours from the engagement
 *  digest's best-time analysis, but cold brands have no digest yet
 *  and were defaulting to generic hours. The briefing captures Mag's
 *  initial read on the audience's day pattern (or the founder's
 *  ground-truth if they have it) so the first plan posts at sensible
 *  times instead of arbitrary ones. */
export const channelIntakePostingWindowSchema = z.object({
    /** Hour-of-day in brand tz (0-23). 9 = 9am local, 19 = 7pm local. */
    hour: z.number().int().min(0).max(23),
    /** Mag's one-line rationale — "lunch break, mobile-heavy",
     *  "post-work scroll", "morning commute". Persisted so the next
     *  re-brief / receipts panel can quote it back. */
    reason: z.string().min(1).max(140),
});
/** One content pillar the founder approved during intake — the agent
 *  proposes 2-4 of these; founder edits / accepts. Pillars later
 *  drive the content-plan weights. */
export const channelIntakePillarSchema = z.object({
    label: z.string().min(1).max(60),
    description: z.string().min(1).max(280),
    weight_percent: z.number().int().min(5).max(80),
});
/** Per-turn output from the channel-intake agent. Discriminated on
 *  decision: 'ask' = keep chatting, 'propose' = mag has enough signal
 *  and is presenting the config + first-week arc for approval. */
export const channelIntakeAgentResultSchema = z.discriminatedUnion('decision', [
    z.object({
        decision: z.literal('ask'),
        /** Mag's next question in her voice. Lowercase, ≤4000 chars. */
        reply: z.string().min(1).max(4000),
    }),
    z.object({
        decision: z.literal('propose'),
        /** Mag's closing line — "ok, here's what i heard. shipping
         *  starts when you approve." Goes into the transcript before
         *  the proposal block renders. */
        reply: z.string().min(1).max(4000),
        /** The complete config bundle the founder will approve. Maps
         *  1:1 onto the side-effects in the finalize endpoint:
         *  brand_brain.identity.business_shape, brand_channels controls,
         *  brand_content_plans seed values. */
        proposal: z.object({
            /** What kind of business this is + whether there's a physical
             *  place worth showcasing. Persists onto brand_brain.identity. */
            business_shape: businessShapeSchema,
            /** Cadence — posts_per_week + tz. */
            cadence: channelIntakeCadenceSchema,
            /** When the founder wants the first post to go live, as a
             *  brand-tz date key (YYYY-MM-DD). null = "as soon as the review
             *  window allows" — mag's default when the founder doesn't name a
             *  day. The content-plan scheduler FLOORS this against the
             *  approval-lead window: a date earlier than the lead permits is
             *  bumped to the earliest legal date, and mag flags that in her
             *  closing line. Optional + nullable for backwards-compat with
             *  proposals authored before this field existed. */
            first_post_date: z
                .string()
                .regex(/^\d{4}-\d{2}-\d{2}$/u, 'first_post_date must be YYYY-MM-DD')
                .nullable()
                .default(null),
            /** Audience-aligned posting windows (s216-54h.69). 1-3 hours
             *  in brand tz when this audience is most likely to engage.
             *  Mag proposes based on the brand's customer + category +
             *  founder's read; the content-plan agent uses these as the
             *  default hour pool until the engagement digest builds enough
             *  history to take over. Optional on the schema for backwards
             *  compatibility with legacy proposals, but the system prompt
             *  requires the agent to fill it. */
            posting_windows: z.array(channelIntakePostingWindowSchema).min(1).max(3).default([]),
            /** 2-4 content pillars with weights. Weights should sum to
             *  100, but the finalize step normalizes if the agent is off. */
            pillars: z.array(channelIntakePillarSchema).min(2).max(4),
            /** Goals the founder named for the next 30 days. Short bullets
             *  the content-plan agent reads as steering signal. */
            goals: z.array(z.string().min(1).max(160)).min(1).max(4),
            /** Founder named voice anchors — phrases / tone notes the
             *  drafter should mirror. Optional; empty when the founder
             *  didn't volunteer anything beyond what's in the brain. */
            voice_anchors: z.array(z.string().min(1).max(200)).max(6).default([]),
            /** Authority tier the founder picked. */
            authority_tier: channelAuthorityTierSchema,
            /** Hours before scheduled publish that mag pings the founder
             *  for approval (when authority_tier !== 'full_autopilot').
             *  Mirrors brand_channels.autopilot_lead_time_hours. */
            review_window_hours: z.number().int().min(2).max(168),
            /** True when the founder said "i have a designer" — the
             *  finalize step flips channel.route_to_designer accordingly.
             *  Designer-role wiring lands in a follow-up session; for now
             *  this just stamps a hint. */
            designer_in_loop: z.boolean(),
            /** Mag's distilled brief — what she heard, in her own words.
             *  Persisted on the channel row + threaded to the content-plan
             *  agent so it doesn't re-derive context. */
            brief_summary: z.string().min(1).max(2000),
        }),
    }),
]);
/** State persisted on brand_channels.intake_state. Carries the
 *  transcript + the latest agent result so a refresh resumes mid-
 *  conversation. Null when no intake has been started. After
 *  finalize, completed_at is stamped — the row stays for receipts /
 *  re-intake context but the dashboard reads it as "done, here's
 *  what mag heard last time." */
export const channelIntakeStateSchema = z.object({
    /** Full chat history. Last entry is mag's most-recent turn unless
     *  the founder just submitted and the agent hasn't replied yet. */
    messages: z.array(channelIntakeMessageSchema).min(1).max(40),
    /** Mag's latest decision. When kind === 'propose', the dashboard
     *  renders the proposal preview alongside the transcript so the
     *  founder can approve / edit without scrolling back. */
    latest_result: channelIntakeAgentResultSchema.nullable(),
    /** ISO timestamp the intake was started. Lets the api enforce a
     *  "stale conversations get discarded after 7d" rule downstream. */
    started_at: z.string().datetime(),
    /** ISO timestamp the founder approved the proposal + the channel
     *  flipped to operated. Null while the intake is still in progress.
     *  Non-null = the row is a receipt of what mag heard, kept so the
     *  next plan refresh + future re-intake can quote back. */
    completed_at: z.string().datetime().nullable().default(null),
});
//# sourceMappingURL=channel-intake.js.map